import { message } from 'antd';
import { ENTITY_ADD_REQUESTS_CRUD_ACTION_CREATORS } from '../actions/entity_add_requests_actions';
import RestCrudThunks from './restCrudThunksGenerator';
import { ROLE_TYPES } from '../utils/DataConstants';
import { getBackendUri } from '../utils/EnvConfigUtils';
import FetchUtils, { parseJSON } from './fetch_utils';
import { logoutSuccess } from '../actions/session_actions';
import { ENTITY_ADD_REQUEST_STATUSES } from '../types/entity_add_requests';

const fetchUtils = new FetchUtils();
const BASE_URL = getBackendUri();

// All entity-add-request endpoints are buyer-routed only in this branch. The curried factory
// keeps a single import surface (per the withUserType convention) while remaining buyer-only.
export const entityAddRequestsRestCrudThunksForBuyer = new RestCrudThunks(
	'entity_add_requests',
	'entity_add_request',
	'entityAddRequest',
	'entityAddRequests',
	ENTITY_ADD_REQUESTS_CRUD_ACTION_CREATORS,
	null,
	ROLE_TYPES.BUYER
);

export const entityAddRequestsRestCrudThunks = (userType) =>
	entityAddRequestsRestCrudThunksForBuyer;

const JSON_HEADERS = { Accept: 'application/json', 'Content-Type': 'application/json' };

// Draft/rejected -> pending_review. No body.
export const submitEntityAddRequest = (userType) => (id) => (dispatch) => {
	const apiUrl = `/api/v1/${userType}/entity_add_requests/${id}/submit`;
	return new Promise((resolve, reject) => {
		fetchUtils
			.put(BASE_URL + apiUrl, { headers: JSON_HEADERS, data: {} })
			.then(parseJSON)
			.then(({ json, status }) => {
				const response = json;
				if (status === 401) {
					dispatch(logoutSuccess());
				}
				if (response.status === 'error') {
					dispatch(ENTITY_ADD_REQUESTS_CRUD_ACTION_CREATORS.updateError(response.message, { id }));
					reject(response.message);
				} else {
					dispatch(ENTITY_ADD_REQUESTS_CRUD_ACTION_CREATORS.updateSuccess(response.data));
					resolve(response.data);
				}
			})
			.catch((error) => {
				dispatch(ENTITY_ADD_REQUESTS_CRUD_ACTION_CREATORS.updateError(error.message, { id }));
				reject(error.message);
			});
	});
};

// pending_review -> approved. Optional payload override; on success the record carries
// createdEntityType / createdEntityId for the "View" deep link.
export const approveEntityAddRequest = (userType) => (id, payloadOverride) => (dispatch) => {
	const apiUrl = `/api/v1/${userType}/entity_add_requests/${id}/approve`;
	const body = payloadOverride ? { payload: payloadOverride } : {};
	return new Promise((resolve, reject) => {
		fetchUtils
			.put(BASE_URL + apiUrl, { headers: JSON_HEADERS, data: body })
			.then(parseJSON)
			.then(({ json, status }) => {
				const response = json;
				if (status === 401) {
					dispatch(logoutSuccess());
				}
				if (response.status === 'error') {
					dispatch(ENTITY_ADD_REQUESTS_CRUD_ACTION_CREATORS.updateError(response.message, { id }));
					reject(response.message);
				} else {
					dispatch(ENTITY_ADD_REQUESTS_CRUD_ACTION_CREATORS.updateSuccess(response.data));
					resolve(response.data);
				}
			})
			.catch((error) => {
				dispatch(ENTITY_ADD_REQUESTS_CRUD_ACTION_CREATORS.updateError(error.message, { id }));
				reject(error.message);
			});
	});
};

// pending_review -> rejected. reason is required (non-empty).
export const rejectEntityAddRequest = (userType) => (id, reason) => (dispatch) => {
	const apiUrl = `/api/v1/${userType}/entity_add_requests/${id}/reject`;
	return new Promise((resolve, reject) => {
		fetchUtils
			.put(BASE_URL + apiUrl, { headers: JSON_HEADERS, data: { reason } })
			.then(parseJSON)
			.then(({ json, status }) => {
				const response = json;
				if (status === 401) {
					dispatch(logoutSuccess());
				}
				if (response.status === 'error') {
					dispatch(ENTITY_ADD_REQUESTS_CRUD_ACTION_CREATORS.updateError(response.message, { id }));
					reject(response.message);
				} else {
					dispatch(ENTITY_ADD_REQUESTS_CRUD_ACTION_CREATORS.updateSuccess(response.data));
					resolve(response.data);
				}
			})
			.catch((error) => {
				dispatch(ENTITY_ADD_REQUESTS_CRUD_ACTION_CREATORS.updateError(error.message, { id }));
				reject(error.message);
			});
	});
};

export const fetchEntityAddRequestAudits = (userType) => (id) => (dispatch) => {
	const apiUrl = `/api/v1/${userType}/entity_add_requests/${id}/audits`;
	return new Promise((resolve, reject) => {
		fetchUtils
			.get(BASE_URL + apiUrl, { headers: JSON_HEADERS })
			.then(parseJSON)
			.then(({ json, status }) => {
				const response = json;
				if (status === 401) {
					dispatch(logoutSuccess());
				}
				if (response.status === 'error') {
					reject(response.message);
				} else {
					resolve(response.data);
				}
			})
			.catch((error) => {
				reject(error.message);
			});
	});
};

// Populates stateSlice.counts[status] for the pipeline stepper via a single count_by_status
// call that returns one row per requested status, sharing the list's base filters (minus any
// active status filter). One row per status is always returned, including zeros.
export const refreshEntityAddRequestCounts = (filters) => (dispatch) => {
	const apiUrl = `/api/v1/${ROLE_TYPES.BUYER}/entity_add_requests/count_by_status`;
	const data = {
		...(filters || {}),
		statuses: Object.keys(ENTITY_ADD_REQUEST_STATUSES).join(','),
		status: undefined,
	};
	fetchUtils
		.get(BASE_URL + apiUrl, Object.assign({ headers: JSON_HEADERS }, data))
		.then(parseJSON)
		.then(({ json }) => {
			const response = json;
			if (response.status === 'error') {
				message.error(response.message);
			} else if (response.data && response.data.length > 0) {
				response.data.forEach((category) =>
					dispatch(
						ENTITY_ADD_REQUESTS_CRUD_ACTION_CREATORS.countBySuccess(
							category.count,
							category.categoryName
						)
					)
				);
			}
		})
		.catch((error) => {
			message.error(error.message);
		});
};
